/**
 * TodayCard — de "Klok"-weergave in een outline-ring. Via de widget-instellingen
 * kies je onafhankelijk (checkboxes): digitale klok, aftellen tot einde werkdag, en
 * aftellen tot een datum (met eigen naam). Alle drie kunnen tegelijk of los aan.
 * Ook de "vandaag"-stand van verborgen game-widgets. 'compact' verkleint alles.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Checkbox, FormControlLabel, TextField } from '@mui/material';
import { T } from './widgetTokens';

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const pad = (n) => String(n).padStart(2, '0');

const RING = {
  height: '100%', minHeight: 96, borderRadius: '0.75rem', p: 2, color: T.text,
  border: `1.5px solid ${T.surfaceHigh}`,
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.25,
};

// Bepaal welke onderdelen aan staan (met migratie van het oude `style`-veld).
function resolveEnabled(s) {
  const legacy = !('showDigital' in s) && !('showWorkday' in s) && !('showVacation' in s);
  let d, w, v;
  if (legacy) {
    d = !s.style || s.style === 'digital' || s.style === 'analog';
    w = s.style === 'workday';
    v = s.style === 'vacation';
  } else {
    d = !!s.showDigital; w = !!s.showWorkday; v = !!s.showVacation;
  }
  if (!d && !w && !v) d = true;
  return { showDigital: d, showWorkday: w, showVacation: v };
}

function Digital({ now, compact }) {
  const hhmm = now.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
  const ss = now.toLocaleTimeString('nl-NL', { second: '2-digit' });
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: compact ? 0.6 : 1 }}>
        <Typography sx={{ fontFamily: T.font, fontSize: compact ? '2.1rem' : { xs: '3.4rem', md: '3.8rem' }, fontWeight: 800, lineHeight: 1, letterSpacing: '-0.02em', color: T.text }}>{hhmm}</Typography>
        <Typography sx={{ fontFamily: T.font, fontSize: compact ? '1rem' : '1.6rem', fontWeight: 700, color: T.outlineVariant }}>{ss}</Typography>
      </Box>
      <Typography sx={{ fontFamily: T.fontBody, fontSize: compact ? '0.82rem' : '1.25rem', fontWeight: 600, color: T.secondary, mt: compact ? 0.4 : 1 }}>{cap(now.toLocaleDateString('nl-NL', { weekday: 'long' }))}</Typography>
      <Typography sx={{ fontFamily: T.fontBody, fontSize: compact ? '0.72rem' : '1rem', color: T.outline }}>{now.toLocaleDateString('nl-NL', { day: 'numeric', month: compact ? 'short' : 'long', year: 'numeric' })}</Typography>
    </Box>
  );
}

function Countdown({ diffMs, title, doneLabel, compact }) {
  if (diffMs <= 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography sx={{ fontSize: compact ? '1.6rem' : '2.2rem', lineHeight: 1 }}>{doneLabel.emoji}</Typography>
        <Typography sx={{ fontFamily: T.font, fontSize: compact ? '0.95rem' : '1.2rem', fontWeight: 800, color: T.text, mt: 0.5 }}>{doneLabel.text}</Typography>
      </Box>
    );
  }
  const totalSec = Math.floor(diffMs / 1000);
  const days = Math.floor(totalSec / 86400);
  const hrs = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Typography sx={{ fontFamily: T.fontBody, fontSize: compact ? '0.6rem' : '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.outline, mb: 0.5 }}>{title}</Typography>
      {days > 0 && (
        <Typography sx={{ fontFamily: T.font, fontSize: compact ? '2rem' : '3rem', fontWeight: 800, lineHeight: 1, color: T.text }}>
          {days}<Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: compact ? '0.8rem' : '1rem', fontWeight: 600, color: T.secondary, ml: 0.75 }}>{days === 1 ? 'dag' : 'dagen'}</Typography>
        </Typography>
      )}
      <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: days > 0 ? (compact ? '0.9rem' : '1.1rem') : (compact ? '1.7rem' : '2.6rem'), fontWeight: 700, color: days > 0 ? T.secondary : T.text, mt: days > 0 ? 0.4 : 0, letterSpacing: '0.02em' }}>
        {pad(hrs)}:{pad(mins)}:{pad(secs)}
      </Typography>
    </Box>
  );
}

export function ClockSettings({ settings, onChange }) {
  const { showDigital, showWorkday, showVacation } = resolveEnabled(settings);
  const commit = (patch) => onChange({ showDigital, showWorkday, showVacation, ...patch });
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 220 }}>
      <Typography sx={{ fontFamily: T.font, fontWeight: 700, fontSize: '0.85rem', color: T.text, mb: 0.5 }}>Klok tonen</Typography>
      <FormControlLabel sx={{ m: 0 }} control={<Checkbox size="small" checked={showDigital} onChange={(e) => commit({ showDigital: e.target.checked })} />}
        label={<Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem' }}>Digitale klok</Typography>} />
      <FormControlLabel sx={{ m: 0 }} control={<Checkbox size="small" checked={showWorkday} onChange={(e) => commit({ showWorkday: e.target.checked })} />}
        label={<Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem' }}>Einde werkdag</Typography>} />
      {showWorkday && (
        <TextField label="Einde werkdag" type="time" size="small" value={settings.workdayEnd || '17:00'}
          onChange={(e) => commit({ workdayEnd: e.target.value })} InputLabelProps={{ shrink: true }} sx={{ ml: 3.5, mb: 0.5 }} />
      )}
      <FormControlLabel sx={{ m: 0 }} control={<Checkbox size="small" checked={showVacation} onChange={(e) => commit({ showVacation: e.target.checked })} />}
        label={<Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem' }}>Aftellen tot datum</Typography>} />
      {showVacation && (
        <Box sx={{ ml: 3.5, display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
          <TextField label="Naam (waarnaartoe)" size="small" value={settings.vacationName || ''} placeholder="bv. vakantie"
            onChange={(e) => commit({ vacationName: e.target.value })} />
          <TextField label="Datum" type="date" size="small" value={settings.vacationDate || ''}
            onChange={(e) => commit({ vacationDate: e.target.value })} InputLabelProps={{ shrink: true }} />
        </Box>
      )}
    </Box>
  );
}

export default function TodayCard({ settings = {}, widget, compact: compactProp }) {
  const widgetCompact = compactProp ?? (widget?.h === 'compact');
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const { showDigital, showWorkday, showVacation } = resolveEnabled(settings);

  const workdayDiff = useMemo(() => {
    const [hh, mm] = (settings.workdayEnd || '17:00').split(':').map(Number);
    const target = new Date(now); target.setHours(hh || 17, mm || 0, 0, 0);
    return target.getTime() - now.getTime();
  }, [now, settings.workdayEnd]);

  const vacationDiff = useMemo(() => {
    if (!settings.vacationDate) return null;
    return new Date(`${settings.vacationDate}T00:00:00`).getTime() - now.getTime();
  }, [now, settings.vacationDate]);

  const count = (showDigital ? 1 : 0) + (showWorkday ? 1 : 0) + (showVacation ? 1 : 0);
  const compact = widgetCompact || count > 1;

  const blocks = [];
  if (showDigital) blocks.push(<Digital key="d" now={now} compact={compact} />);
  if (showWorkday) blocks.push(<Countdown key="w" diffMs={workdayDiff} title="tot einde werkdag" doneLabel={{ emoji: '🎉', text: 'Werkdag klaar!' }} compact={compact} />);
  if (showVacation) {
    blocks.push(settings.vacationDate
      ? <Countdown key="v" diffMs={vacationDiff} title={`tot ${settings.vacationName || 'de datum'}`} doneLabel={{ emoji: '🌴', text: 'Het is zover!' }} compact={compact} />
      : <Typography key="v" sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.outline, textAlign: 'center', px: 2 }}>Stel een datum in via ⚙</Typography>);
  }

  return (
    <Box sx={{ display: 'flex', height: '100%' }}>
      <Box sx={{ ...RING, flex: 1 }}>
        {blocks.map((b, i) => (
          <React.Fragment key={b.key || i}>
            {i > 0 && <Box sx={{ width: '55%', borderTop: `1px solid ${T.surfaceHigh}` }} />}
            {b}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}
